/**
 * Pure-logic helpers for the shared widget data-contract framework.
 *
 * Kept in a separate file from the React-rendering layer so the unit
 * tests can import it via `node --test` without dragging React +
 * @tanstack/react-query into the runtime resolution graph.
 *
 * Public surface:
 *
 *   createWidgetDescriptor(input)      - fills sensible defaults on the
 *                                        WidgetDescriptor shape from P2A.7
 *                                        so module manifests stay terse
 *   __resetWidgetCacheForTests()       - clears the memory + session caches
 *                                        used by useWidgetData
 *   __cacheRead / __cacheWrite         - test-visible cache primitives
 */

import type {
  WidgetDescriptor,
  WidgetSize,
  WidgetRefreshPolicy,
} from '@tyndo/types';

// ────────────────────────────────────────────────────────────────────────
// 5-state discriminated union — every widget surfaces one of these.
// ────────────────────────────────────────────────────────────────────────

export type WidgetDataSourceFlavor = 'live' | 'cached' | 'simulator' | 'stale';

export type WidgetDataState<T> =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error'; error: { message: string; statusCode?: number } }
  | { status: 'permission-denied' }
  | { status: 'ready'; data: T; source: WidgetDataSourceFlavor };

// ────────────────────────────────────────────────────────────────────────
// Cache policy — extends useApiQuery with a memory + session cache.
// ────────────────────────────────────────────────────────────────────────

export type WidgetCachePolicy = 'memory' | 'session' | 'none';

interface CacheEntry {
  data: unknown;
  ts: number;
}

const memoryCache = new Map<string, CacheEntry>();

export function __cacheRead(policy: WidgetCachePolicy, key: string): unknown | null {
  if (policy === 'none') return null;
  if (policy === 'memory') {
    const hit = memoryCache.get(key);
    return hit ? hit.data : null;
  }
  // session
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    return parsed?.data ?? null;
  } catch {
    return null;
  }
}

export function __cacheWrite(policy: WidgetCachePolicy, key: string, data: unknown): void {
  if (policy === 'none') return;
  const entry: CacheEntry = { data, ts: Date.now() };
  if (policy === 'memory') {
    memoryCache.set(key, entry);
    return;
  }
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    /* quota / disabled — silent */
  }
}

/**
 * Visible for tests. Resets the in-memory + session caches.
 */
export function __resetWidgetCacheForTests(): void {
  memoryCache.clear();
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.clear();
  } catch {
    /* ignore */
  }
}

// ────────────────────────────────────────────────────────────────────────
// createWidgetDescriptor — fills sensible defaults so manifests stay terse.
// ────────────────────────────────────────────────────────────────────────

export interface CreateWidgetDescriptorInput
  extends Omit<
    WidgetDescriptor,
    'supportedSizes' | 'defaultSize' | 'refreshPolicy' | 'resizable' | 'version'
  > {
  supportedSizes?: WidgetSize[];
  defaultSize?: WidgetSize;
  refreshPolicy?: WidgetRefreshPolicy;
  resizable?: boolean;
  version?: string;
}

const DEFAULT_SUPPORTED_SIZES: WidgetSize[] = ['small', 'medium'];

/**
 * Fills sensible defaults on the WidgetDescriptor shape:
 *
 *   - supportedSizes: ['small', 'medium']
 *   - defaultSize:    first of supportedSizes
 *   - resizable:      true
 *   - refreshPolicy:  realtime when dataSource.realtimeTopic is set,
 *                     poll-{pollMs} when dataSource.pollMs is set,
 *                     else poll-60s
 *   - version:        '1.0.0'
 */
export function createWidgetDescriptor(input: CreateWidgetDescriptorInput): WidgetDescriptor {
  const supportedSizes = input.supportedSizes ?? DEFAULT_SUPPORTED_SIZES;
  const defaultSize = input.defaultSize ?? supportedSizes[0] ?? 'medium';

  let refreshPolicy: WidgetRefreshPolicy | undefined = input.refreshPolicy;
  if (!refreshPolicy) {
    if (input.dataSource?.realtimeTopic) refreshPolicy = { kind: 'realtime' };
    else if (input.dataSource?.pollMs && input.dataSource.pollMs > 0)
      refreshPolicy = { kind: 'poll', intervalMs: input.dataSource.pollMs };
    else refreshPolicy = { kind: 'poll', intervalMs: 60000 };
  }

  return {
    ...input,
    resizable: input.resizable ?? true,
    version: input.version ?? '1.0.0',
    refreshPolicy,
    supportedSizes,
    defaultSize,
  };
}
